import { keyframes } from '@emotion/react';
import { Box, Stack } from 'components/ui';

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

interface SkeletonProps {
  height: string;
  width?: string;
  mb?: number;
}

const Skeleton: React.FC<SkeletonProps> = ({ height, width = '100%', mb }) => (
  <Box
    height={height}
    width={width}
    bg={{ base: 'gray.200', _dark: 'gray.700' }}
    borderRadius="md"
    mb={mb}
    css={{
      animation: `${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`
    }}
  />
);

interface ChartSkeletonProps {
  height?: number;
  withTitle?: boolean;
  withLegend?: boolean;
}

export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ 
  height = 250, 
  withTitle = false,
  withLegend = false 
}) => {
  return (
    <Box>
      {withTitle && (
        <Skeleton height="24px" width="200px" mb={4} />
      )}
      
      {withLegend && (
        <Stack direction="row" gap={4} mb={4}>
          <Skeleton height="12px" width="80px" />
          <Skeleton height="12px" width="80px" />
        </Stack>
      )}
      
      <Box position="relative" width="100%" height={`${height}px`}>
        {/* Y-axis */}
        <Box position="absolute" left={0} top={0} bottom={0} width="40px">
          <Stack gap={3} height="100%" justify="space-between" py={2}>
            <Skeleton height="10px" width="30px" />
            <Skeleton height="10px" width="30px" />
            <Skeleton height="10px" width="30px" />
            <Skeleton height="10px" width="30px" />
          </Stack>
        </Box>
        
        {/* Chart area with bars/lines */}
        <Box position="absolute" left="50px" right="20px" top="10px" bottom="40px">
          <Stack direction="row" gap={2} height="100%" align="flex-end">
            <Skeleton height="60%" />
            <Skeleton height="80%" />
            <Skeleton height="45%" />
            <Skeleton height="90%" />
            <Skeleton height="70%" />
            <Skeleton height="55%" />
            <Skeleton height="85%" />
            <Skeleton height="65%" />
            <Skeleton height="75%" />
            <Skeleton height="50%" />
          </Stack>
        </Box>
        
        {/* X-axis labels */}
        <Box position="absolute" left="50px" right="20px" bottom="10px">
          <Stack direction="row" justify="space-between">
            <Skeleton height="10px" width="30px" />
            <Skeleton height="10px" width="30px" />
            <Skeleton height="10px" width="30px" />
            <Skeleton height="10px" width="30px" />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
